import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import CustomPagination from '../../../components/customPagination';
import SubscriptionInvoiceUsageCell from './SubscriptionInvoiceUsageCell';
import { formatEpochDateTime } from '../../../utils/date';

export default function SubscriptionInvoicesTable({
  subscriptionId,
  invoices,
  totalDocuments,
  isLoading,
  handlePaginationChange,
}) {
  const handleCopyId = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      // Ignore clipboard errors to avoid blocking table interactions.
    }
  };

  const formatCurrency = (amount, currency) => {
    if (typeof amount !== 'number') return '-';
    if (!currency) return amount;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const columns = [
    {
      field: 'stripe_invoice_id',
      headerName: 'Stripe Invoice ID',
      flex: 1,
      renderCell: (params) => {
        const value = params.value;

        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%', height: '100%' }}
          >
            <Typography
              variant="body2"
              title={value || '-'}
              sx={{
                minWidth: 0,
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value || '-'}
            </Typography>
            <Tooltip title="Copy ID">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ ml: 0.5, flexShrink: 0 }}
                  aria-label={`copy-${value || 'id'}`}
                  disabled={!value}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyId(value);
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.5,
      valueGetter: (_value, row) => formatCurrency(row?.amount, row?.currency),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      renderCell: (params) => {
        const status = params.value || '-';
        const color = status === 'paid' ? 'success' : 'default';

        return <Chip label={status} size="small" color={color} />;
      },
    },
    {
      field: 'document_usage',
      headerName: 'Document Usage',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <SubscriptionInvoiceUsageCell subscriptionId={subscriptionId} stripeInvoiceId={params.row?.stripe_invoice_id} />
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      flex: 0.7,
      valueFormatter: (value) => formatEpochDateTime(value),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 0.7,
      valueFormatter: (value) => formatEpochDateTime(value),
    },
    {
      field: 'hosted_invoice_url',
      headerName: 'Payment',
      flex: 0.3,
      renderCell: (params) => {
        const paymentUrl = params.value;
        const isPaid = params.row?.status === 'paid';
        if (!paymentUrl || isPaid) return '-';

        return (
          <Link href={paymentUrl} target="_blank" rel="noopener noreferrer" underline="hover">
            Pay Now
          </Link>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={invoices}
        columns={columns}
        getRowId={(row) => row.stripe_invoice_id}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10, 25]}
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => handlePaginationChange(model.page, model.pageSize)}
        slots={{ pagination: CustomPagination }}
        getRowHeight={() => 'auto'}
      />
    </Box>
  );
}
